// src/components/EditableQuotation.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Image,
} from "@react-pdf/renderer";

// PDF Styles (same as ProfessionalVATInvoice)
const pdfStyles = StyleSheet.create({
  page: {
    padding: 10,
    fontSize: 8,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#0891b2",
    paddingBottom: 20,
  },
  companyName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0891b2",
  },
  companyDetails: {
    fontSize: 9,
    color: "#333",
    marginTop: 2,
  },
  invoiceTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0891b2",
    textAlign: "right",
  },
  subtitle0: {
    fontSize: 12,
    color: "#666",
    textAlign: "right",
    marginTop: 5,
    fontWeight: "bold",
  },
  quotationTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0891b2",
  },
  subtitle: {
    fontSize: 10,
    color: "#666",
    textAlign: "right",
    marginTop: 5,
  },
  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
    backgroundColor: "#ffffff",
    padding: 10,
  },
  infoBlock: {
    flex: 1,
    marginRight: 15,
  },
  infoLabel: {
    fontSize: 9,
    color: "#333",
    marginBottom: 2,
    textTransform: "uppercase",
    fontWeight: "bold",
  },
  infoValue: {
    fontSize: 11,
    fontWeight: "600",
    color: "#000",
  },
  customerSection: {
    marginBottom: 20,
    padding: 10,
    backgroundColor: "#ffffff",
  },
  table: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#ddd",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0891b2",
    color: "white",
    padding: 10,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    padding: 8,
  },
  tableRowAlt: {
    backgroundColor: "#f9f9f9",
  },
  col1: { width: "30%", paddingHorizontal: 5 },
  col2: { width: "8%", textAlign: "center", paddingHorizontal: 5 },
  col3: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col4: { width: "12%", textAlign: "right", paddingHorizontal: 5 },
  col5: { width: "12%", textAlign: "right", paddingHorizontal: 5 },
  col6: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col7: { width: "12%", textAlign: "right", paddingHorizontal: 5 },
  discountTotalRow: {
    flexDirection: "row",
    borderBottomWidth: 2,
    borderBottomColor: "#0891b2",
    backgroundColor: "#f3f4f6",
    padding: 8,
    fontWeight: "bold",
  },
  totals: {
    marginTop: 20,
    alignItems: "flex-end",
  },
  totalRow: {
    flexDirection: "row",
    padding: 5,
    width: "100%",
  },
  totalLabel: {
    flex: 1,
    textAlign: "right",
    marginRight: 10,
    fontSize: 11,
  },
  totalValue: {
    width: 120,
    textAlign: "right",
    fontSize: 11,
  },
  grandTotal: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#0891b2",
    borderTopWidth: 2,
    borderTopColor: "#0891b2",
    paddingTop: 10,
    marginTop: 5,
  },
  notes: {
    marginTop: 30,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  notesTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#0891b2",
  },
  notesText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: "#666",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    color: "#666",
    fontSize: 9,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    paddingTop: 10,
  },
  compliance: {
    fontSize: 8,
    color: "#0891b2",
    marginTop: 5,
  },
  logo: {
    width: 30,
    height: 30,
  },
});

// PDF Document Component
const QuotationPDFDocument = ({ quotationData }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: "AED",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.header}>
          <View>
            {quotationData.company.logo && (
              <Image src={quotationData.company.logo} style={{ width: 100, height: 120, marginBottom: 5, marginLeft: 100 }} />
            )}
          </View>
          <View>
            <Text style={pdfStyles.invoiceTitle}>QUOTATION</Text>
            <Text style={pdfStyles.subtitle0}>{quotationData.company.name}</Text>
            <Text style={pdfStyles.subtitle}>TRN: 104836477000001</Text>
                   <Text  style={pdfStyles.subtitle}>Sharjah - Muweilah - University City Street</Text> 
              <Text  style={pdfStyles.subtitle}>Office No.: 602-58 Floor No.: 6 - Al Areej</Text>
              <Text  style={pdfStyles.subtitle}>0557001522</Text>
              <Text  style={pdfStyles.subtitle}>dqcc01@gmail.com</Text>
              <Text  style={pdfStyles.subtitle}>www.dqcc.ae</Text>
          </View>
        </View>

        <View style={pdfStyles.infoSection}>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Quotation Number</Text>
            <Text style={pdfStyles.infoValue}>{quotationData.quotationNo}</Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Quotation Date</Text>
            <Text style={pdfStyles.infoValue}>
              {formatDate(quotationData.quotationDate)}
            </Text>
          </View>
       <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Quotation Value</Text>
            <Text style={pdfStyles.infoValue}>{formatCurrency(quotationData.totals.totalDue)}</Text>
          </View>
        </View>

        <View style={pdfStyles.customerSection}>
          <Text style={pdfStyles.infoLabel}>Quoted For:</Text>
          <Text style={[pdfStyles.infoValue, { marginTop: 5, marginBottom: 3 }]}>
            {quotationData.customerName}
          </Text>
         
          <Text style={pdfStyles.companyDetails}>
            {quotationData.customerAddress}
          </Text>
          
          <Text style={pdfStyles.companyDetails}>
            {quotationData.customerCity}, {quotationData.customerCountry}
          </Text>
          {quotationData.customerPhone && (
            <Text style={pdfStyles.companyDetails}>
              Phone: {quotationData.customerPhone}
            </Text>
          )}
          {quotationData.customerTrn && (
            <Text style={pdfStyles.companyDetails}>
              TRN: {quotationData.customerTrn}
            </Text>
          )}
        </View>

        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableHeader}>
            <Text style={pdfStyles.col1}>ITEM DESCRIPTION</Text>
            <Text style={pdfStyles.col2}>QTY.</Text>
            <Text style={pdfStyles.col3}>UNIT</Text>
            <Text style={pdfStyles.col4}>PRICE</Text>
            <Text style={pdfStyles.col5}>DISCOUNT</Text>
            <Text style={pdfStyles.col6}>VAT %</Text>
            <Text style={pdfStyles.col7}>TOTAL</Text>
          </View>

          {quotationData.items.map((item, index) => {
            const quantity = parseFloat(item.quantity || 0);
            const unitPrice = parseFloat(item.unit_price || 0);
            const discountAmount = parseFloat(item.discount_amount || 0);
            const vatPercent = parseFloat(item.vat_percent || 0);
            const subtotal = quantity * unitPrice;
            const afterDiscount = subtotal - discountAmount;
            const vatAmount = afterDiscount * (vatPercent / 100);
            const total = afterDiscount + vatAmount;

            return (
              <View
                key={index}
                style={[
                  pdfStyles.tableRow,
                  index % 2 === 1 ? pdfStyles.tableRowAlt : {},
                ]}
              >
                <Text style={pdfStyles.col1}>{item.description}</Text>
                <Text style={pdfStyles.col2}>{quantity}</Text>
                <Text style={pdfStyles.col3}>{item.unit || "each"}</Text>
                <Text style={pdfStyles.col4}>{formatCurrency(unitPrice)}</Text>
                <Text style={pdfStyles.col5}>{formatCurrency(discountAmount)}</Text>
                <Text style={pdfStyles.col6}>{vatPercent || 0}%</Text>
                <Text style={pdfStyles.col7}>{formatCurrency(total)}</Text>
              </View>
            );
          })}
          {/* <View style={pdfStyles.discountTotalRow}>
            <Text style={[pdfStyles.col1, pdfStyles.col2, pdfStyles.col3, pdfStyles.col4, { fontSize: 11, fontWeight: "bold" }]}>Total Discount:</Text>
            <Text style={[pdfStyles.col5, { fontSize: 11, fontWeight: "bold", color: "#dc2626" }]}>{formatCurrency(quotationData.totals.totalDiscount)}</Text>
            <Text style={[pdfStyles.col6, pdfStyles.col7]}></Text>
          </View> */}
        </View>

        <View style={pdfStyles.totals}>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Subtotal:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(quotationData.totals.subtotal)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Less: Discount:</Text>
            <Text style={pdfStyles.totalValue}>
              ({formatCurrency(quotationData.totals.totalDiscount)})
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Taxable Amount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(quotationData.totals.taxableAmount)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>VAT (5%):</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(quotationData.totals.vatTotal)}
            </Text>
          </View>
          <View style={[pdfStyles.totalRow, { borderTopWidth: 2, borderTopColor: "#0891b2", paddingTop: 10, marginTop: 5 }]}>
            <Text style={{ flex: 1, textAlign: "right", marginRight: 10, fontSize: 12, fontWeight: "bold", color: "#0891b2" }}>Quoted Amount: (AED)</Text>
            <Text style={[pdfStyles.totalValue, { fontWeight: "bold", fontSize: 12, color: "#0891b2" }]}>
              {formatCurrency(quotationData.totals.totalDue)}
            </Text>
          </View>
        </View>

        {quotationData.notes && (
          <View style={pdfStyles.notes}>
            <Text style={pdfStyles.notesTitle}>Terms & Conditions:</Text>
            <Text style={pdfStyles.notesText}>{quotationData.notes}</Text>
          </View>
        )}

        <View style={pdfStyles.footer}>
          <Text>
            This is a computer generated quotation
          </Text>
          {/* <Text style={pdfStyles.compliance}>
            🇦🇪 UAE Tax Compliant | Professional Quote | TRN:{" "}
            {quotationData.company.trn}
          </Text> */}
        </View>
      </Page>
    </Document>
  );
};

const EditableQuotation = ({
  onSubmit = () => {},
  initialData,
  companyInfo = null,
}) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(
    initialData || {
      id: null,
      quotationNo: "",
      quotationDate: "",
      customerId: null,
      customerName: "",
      customerTrn: "",
      customerPhone: "",
      customerAddress: "",
      customerCity: "",
      customerCountry: "",
      notes: "",
    }
  );

  const [company] = useState(
    companyInfo || {
      name: "Diamond Quality Constructions",
      trn: "104836477000001",

      address: "Sharja, UAE,",
      logo: "/logo.jpeg",
    }
  );

  const [items, setItems] = useState(initialData?.items || []);
  const [errors, setErrors] = useState({});
  const [pdfData, setPdfData] = useState(null);
  const [shouldDownload, setShouldDownload] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  const emirates = [
    "Abu Dhabi",
    "Dubai",
    "Sharjah",
    "Ajman",
    "Umm Al Quwain",
    "Ras Al Khaimah",
    "Fujairah",
  ];

  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const discountAmount = item.discount_amount;
    const afterDiscount = subtotal - discountAmount;
    const vatAmount = afterDiscount * (item.vat_percent / 100);
    return afterDiscount + vatAmount;
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxableAmount = 0;
    let vatTotal = 0;

    items.forEach((item) => {
      const itemSubtotal = item.quantity * item.unit_price;
      const itemDiscount = item.discount_amount;
      const itemAfterDiscount = itemSubtotal - itemDiscount;
      const itemVat = itemAfterDiscount * (item.vat_percent / 100);

      subtotal += itemSubtotal;
      totalDiscount += itemDiscount;
      taxableAmount += itemAfterDiscount;
      vatTotal += itemVat;
    });

    const totalDue = taxableAmount + vatTotal;

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      vatTotal,
      totalDue,
    };
  };

  const { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue } =
    calculateTotals();

  const addItem = () => {
    setItems([
      ...items,
      {
        description: "",
        quantity: 1,
        unit: "Unit",
        unit_price: 0,
        discount_amount: 0,
        vat_percent: 5,
      },
    ]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];

    if (
      ["quantity", "unit_price", "discount_amount", "vat_percent"].includes(
        field
      )
    ) {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }

    setItems(newItems);
  };

  const updateField = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName)
      newErrors.customerName = "Customer name is required";
    if (!formData.quotationDate) newErrors.quotationDate = "Quotation date is required";

    items.forEach((item, index) => {
      if (!item.description) {
        newErrors[`item_${index}_description`] = "Description required";
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = "Quantity must be greater than 0";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowSaveModal(true);
    }
  };

  const saveQuotation = async (downloadPDF = false) => {
    setShowSaveModal(false);
    setIsSubmitting(true);

    try {
      const updatePayload = {
        customer_id: formData.customerId,
        quotation_date: formData.quotationDate,
        notes: formData.notes,
        items: items.map((item) => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_amount: parseFloat(item.discount_amount) || 0,
          vat_percent: parseFloat(item.vat_percent) || 0,
        })),
      };

      const response = await axios.put(
        `${apiUrl}/api/quotations/${formData.id}`,
        updatePayload
      );

      if (response.data && response.data.data) {
        const updatedQuotation = response.data.data;
        setFormData({
          ...formData,
          quotationNo: updatedQuotation.quotation_no || formData.quotationNo,
          quotationDate: updatedQuotation.quotation_date || formData.quotationDate,
          notes: updatedQuotation.notes || formData.notes,
        });
      }

      const pdfQuotationData = {
        ...formData,
        items: items,
        company: company,
        totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
      };

      if (downloadPDF) {
        setPdfData(pdfQuotationData);
        setShouldDownload(true);
      }

      // Just show success message - NO NAVIGATION
      setSuccessMessage(formData.quotationNo);
      
    } catch (error) {
      console.error("Error updating quotation:", error);
      alert(
        error.response?.data?.message ||
          "Error updating quotation. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (shouldDownload && pdfData) {
      const generateAndDownloadPDF = async () => {
        try {
          const pdfDocument = React.createElement(QuotationPDFDocument, { quotationData: pdfData });
          const blob = await pdf(pdfDocument).toBlob();

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          const fileName = `Quotation-${pdfData.quotationNo || "Updated"}.pdf`;
          link.download = fileName;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          setShouldDownload(false);
          setPdfData(null);
        } catch (error) {
          console.error("Error generating PDF:", error);
          alert("Error generating PDF. Please try again.");
          setShouldDownload(false);
          setPdfData(null);
        }
      };

      generateAndDownloadPDF();
    }
  }, [shouldDownload, pdfData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-[#23A19B] rounded-t-2xl p-8 text-white shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">Edit Quotation</h1>
              <p className="text-white/80">
                Quotation #{formData.quotationNo} • Professional Quote
              </p>
            </div>
            <div className="badge badge-lg bg-white/20 text-white border-0 p-4">
              <span className="text-2xl font-bold">✏️</span>
            </div>
          </div>
        </div>

        <div className="bg-base-100 rounded-b-2xl shadow-2xl p-8 mb-8 border-t-4 border-primary">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Quotation Details Section */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                Quotation Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-primary">📄</span> QUOTATION NO.
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 focus:border-primary bg-base-200 cursor-not-allowed"
                    value={formData.quotationNo}
                    readOnly
                    placeholder="QUOT-00001"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cannot be changed
                  </p>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-secondary">📅</span> QUOTATION DATE
                    </span>
                  </label>
                  <input
                    type="date"
                    className={`input input-bordered border-2 focus:border-secondary ${
                      errors.quotationDate ? "input-error" : ""
                    }`}
                    value={formData.quotationDate}
                    onChange={(e) =>
                      updateField("quotationDate", e.target.value)
                    }
                  />
                  {errors.quotationDate && (
                    <span className="text-error text-sm mt-1">
                      {errors.quotationDate}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Customer Section */}
            <div className="bg-gradient-to-r from-secondary/10 to-accent/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-secondary flex items-center gap-2">
                <span className="w-1 h-6 bg-secondary rounded-full"></span>
                Quoted For — Customer
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      CUSTOMER NAME *
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.customerName}
                    readOnly
                    placeholder="Customer name"
                  />
                  <p className="text-xs text-gray-500 mt-1">Cannot be changed</p>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      TRN (TAX REG. NO.)
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 font-mono bg-base-200 cursor-not-allowed"
                    value={formData.customerTrn}
                    readOnly
                    placeholder="15-digit TRN"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">PHONE</span>
                  </label>
                  <input
                    type="tel"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.customerPhone}
                    readOnly
                    placeholder="+971 50 123 4567"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">ADDRESS</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.customerAddress}
                    readOnly
                    placeholder="Street, Area"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      CITY / EMIRATE
                    </span>
                  </label>
                  <select
                    className="select select-bordered border-2 bg-base-200 cursor-not-allowed opacity-75"
                    value={formData.customerCity}
                    disabled
                  >
                    <option value="">{formData.customerCity || "Select..."}</option>
                    {emirates.map((emirate) => (
                      <option key={emirate} value={emirate}>
                        {emirate}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">COUNTRY</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.customerCountry}
                    readOnly
                    placeholder="UAE"
                  />
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="bg-gradient-to-r from-accent/10 to-primary/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-accent mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-accent rounded-full"></span>
                Line Items
              </h3>

              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                    <tr>
                      <th className="rounded-tl-lg">#</th>
                      <th>DESCRIPTION</th>
                      <th>QTY</th>
                      <th>UNIT</th>
                      <th>UNIT PRICE</th>
                      <th>DISC</th>
                      <th>VAT %</th>
                      <th className="rounded-tr-lg">TOTAL</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const itemTotal = calculateItemTotal(item);

                      return (
                        <tr
                          key={index}
                          className="hover:bg-base-200 transition-colors"
                        >
                          <td className="font-bold text-primary">
                            {index + 1}
                           </td>
                          <td>
                            <input
                              type="text"
                              className={`input input-bordered input-sm w-40 ${
                                errors[`item_${index}_description`]
                                  ? "input-error"
                                  : ""
                              }`}
                              value={item.description}
                              onChange={(e) =>
                                updateItem(index, "description", e.target.value)
                              }
                              placeholder="Description"
                            />
                            {errors[`item_${index}_description`] && (
                              <p className="text-error text-xs mt-1">
                                {errors[`item_${index}_description`]}
                              </p>
                            )}
                          </td>
                          <td>
                            <input
                              type="number"
                              className={`input input-bordered input-sm w-16 ${
                                errors[`item_${index}_quantity`]
                                  ? "input-error"
                                  : ""
                              }`}
                              value={item.quantity}
                              onChange={(e) =>
                                updateItem(index, "quantity", e.target.value)
                              }
                              min="1"
                              step="0.01"
                            />
                            {errors[`item_${index}_quantity`] && (
                              <p className="text-error text-xs mt-1">
                                {errors[`item_${index}_quantity`]}
                              </p>
                            )}
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-20"
                              value={item.unit}
                              onChange={(e) =>
                                updateItem(index, "unit", e.target.value)
                              }
                            >
                              <option>Unit</option>
                              <option>Hour</option>
                              <option>Day</option>
                              <option>Service</option>
                              <option>Piece</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-20"
                              value={item.unit_price}
                              onChange={(e) =>
                                updateItem(index, "unit_price", e.target.value)
                              }
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-16"
                              value={item.discount_amount}
                              onChange={(e) =>
                                updateItem(
                                  index,
                                  "discount_amount",
                                  e.target.value
                                )
                              }
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-16"
                              value={item.vat_percent}
                              onChange={(e) =>
                                updateItem(index, "vat_percent", e.target.value)
                              }
                            >
                              <option value="0">0%</option>
                              <option value="5">5%</option>
                            </select>
                          </td>
                          <td className="text-right font-mono font-bold text-success">
                            AED {itemTotal.toFixed(2)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs text-error hover:bg-error/20"
                              onClick={() => removeItem(index)}
                              disabled={items.length === 1}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                className="btn btn-outline btn-accent btn-sm mt-4 hover:scale-105 transition-transform"
                onClick={addItem}
              >
                ➕ Add Line Item
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="stat bg-gradient-to-br from-info/20 to-info/5 rounded-xl">
                <div className="stat-figure text-info">
                  <span className="text-2xl">💰</span>
                </div>
                <div className="stat-title">Subtotal</div>
                <div className="stat-value text-info text-2xl">
                  AED {subtotal.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-warning/20 to-warning/5 rounded-xl">
                <div className="stat-figure text-warning">
                  <span className="text-2xl">🏷️</span>
                </div>
                <div className="stat-title">Discount</div>
                <div className="stat-value text-warning text-2xl">
                  AED {totalDiscount.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-secondary/20 to-secondary/5 rounded-xl">
                <div className="stat-figure text-secondary">
                  <span className="text-2xl">🧾</span>
                </div>
                <div className="stat-title">VAT (5%)</div>
                <div className="stat-value text-secondary text-2xl">
                  AED {vatTotal.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-success/20 to-success/5 rounded-xl">
                <div className="stat-figure text-success">
                  <span className="text-2xl">💵</span>
                </div>
                <div className="stat-title">Quoted Amount</div>
                <div className="stat-value text-success text-2xl">
                  AED {totalDue.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-gradient-to-br from-info/10 to-info/5 p-6 rounded-xl">
                <label className="label">
                  <span className="label-text font-bold text-info text-lg flex items-center gap-2">
                    <span className="w-1 h-6 bg-info rounded-full"></span>
                    TERMS & CONDITIONS
                  </span>
                </label>
                <textarea
                  className="textarea textarea-bordered h-32 w-full border-2 focus:border-info"
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Add any terms or conditions..."
                ></textarea>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-4 pt-6 border-t">
              <button
                type="button"
                className="btn btn-ghost btn-lg hover:bg-base-200"
                onClick={() => window.history.back()}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary btn-lg bg-gradient-to-r from-primary to-secondary border-0 hover:scale-105 transition-transform shadow-lg disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Updating...
                  </>
                ) : (
                  <>
                    ✏️ Update Quotation
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="text-center text-sm text-base-500 mt-4">
          <p>
            🇦🇪 UAE Tax Compliant | Valid for 30 days from date of issue
          </p>
        </div>

        {/* Save Options Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-primary mb-2">
                  Update Quotation
                </h2>
                <p className="text-gray-600">What would you like to do?</p>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => saveQuotation(false)}
                  disabled={isSubmitting}
                  className="w-full btn btn-outline btn-lg border-2 border-primary text-primary hover:bg-primary hover:border-primary disabled:opacity-50"
                >
                  <span className="text-xl mr-2">✏️</span>
                  Update Quotation Only
                </button>

                <button
                  onClick={() => saveQuotation(true)}
                  disabled={isSubmitting}
                  className="w-full btn btn-primary btn-lg bg-gradient-to-r from-primary to-secondary border-0 disabled:opacity-50"
                >
                  <span className="text-xl mr-2">📄</span>
                  Update & Print PDF
                </button>
              </div>

              <div className="mt-6 pt-6 border-t text-center">
                <button
                  onClick={() => setShowSaveModal(false)}
                  disabled={isSubmitting}
                  className="btn btn-ghost btn-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Message Modal - NO NAVIGATION, JUST CLOSES */}
        {successMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="mb-6 text-center">
                <div className="text-6xl text-success mb-4">✓</div>
                <h2 className="text-2xl font-bold text-success mb-2">
                  Quotation Updated Successfully
                </h2>
                <p className="text-lg text-primary font-semibold">
                  Quotation No:{" "}
                  <span className="text-secondary font-bold">
                    {successMessage}
                  </span>
                </p>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="btn btn-primary btn-lg"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableQuotation;